/**
 * The glyph for an agent's status and for its visibility, one definition for
 * every surface that shows either: the agents table, the agent tree and the
 * fields of the settings panel. They used to be a local map per page, which is
 * how the table ended up with one convention and the fields with another.
 *
 * Status speaks the same language as the actions that change it: the row menu
 * activates with a play and deactivates with a pause, so the state says play
 * and pause too, in the same bare glyphs. Nothing tells the two apart on
 * shape, and nothing needs to: the state always carries its own word next to
 * it ("Active", "Paused"), while the action sits in a menu of verbs. A pair of
 * dots, filled and hollow, was tried first and read as nothing at all at this
 * size. Colour is not left to carry the difference on its own: the three would
 * be one badge to anyone who cannot tell green from amber, and a field icon
 * has no colour to begin with.
 *
 * Visibility used to be a padlock for all three values, and the open one sat
 * on "public": that reads as "unprotected", which is both alarming and untrue.
 * The closed padlock on "private" stays, because it is the convention every
 * file service has taught, and the other two say who can see the agent: the
 * globe for anyone, the building for the organization.
 */
export const AGENT_STATUS_GLYPHS: Record<string, string> = {
    active: 'ri:play-line',
    inactive: 'ri:pause-line',
    draft: 'ri:edit-line',
    error: 'ri:error-warning-line',
}

export const AGENT_VISIBILITY_GLYPHS: Record<string, string> = {
    public: 'ri:global-line',
    organization: 'ri:building-line',
    private: 'ri:lock-line',
}

/** The glyph for a status. Falls back to the paused state. */
export function agentStatusGlyph(status?: string): string {
    return AGENT_STATUS_GLYPHS[status ?? ''] ?? 'ri:pause-line'
}

/**
 * The glyph for a visibility. Falls back to the building, which is the
 * schema's own default (`AgentVisibility.Organization`).
 */
export function agentVisibilityGlyph(visibility?: string): string {
    return AGENT_VISIBILITY_GLYPHS[visibility ?? ''] ?? 'ri:building-line'
}
